import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { whatsappActions } from "../slices/whatsapp";
import { getErrorMessage } from "../../utils/errorUtils";

import { WhatsappTemplateRequestDto } from "../../shared/dtos/whatsapp/WhatsappTemplateRequestDto";
import { WhatsappMessageDto } from "../../shared/dtos/whatsapp/WhatsappMessageDto";
import { WhatsappConversationSummaryDto } from "../../shared/dtos/whatsapp/WhatsappConversationSummaryDto";
import { UpdateWhatsappReplyPayload } from "../../shared/interfaces/whatsapp/UpdateWhatsappReplyPayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getConversations =
  (params: PaginationQueryDto = {}) =>
  async (dispatch: AppDispatch) => {
    dispatch(whatsappActions.setLoadingConversations());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<WhatsappConversationSummaryDto>>
      >("/whatsapp/conversations", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(whatsappActions.setConversations(data.data.content));
        dispatch(
          whatsappActions.setConversationsPagination(data.data.pagination),
        );
      }
    } catch (error) {
      dispatch(whatsappActions.setErrorConversations(getErrorMessage(error)));
    }
  };

export const getMessages =
  ({
    conversationId,
    ...params
  }: { conversationId: string } & PaginationQueryDto) =>
  async (dispatch: AppDispatch) => {
    dispatch(whatsappActions.setLoadingMessages());
    dispatch(whatsappActions.setActiveConversationId(conversationId));
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<WhatsappMessageDto>>
      >(`/whatsapp/conversations/${conversationId}/messages`, {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(whatsappActions.setMessages(data.data.content));
        dispatch(whatsappActions.setMessagesPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(whatsappActions.setErrorMessages(getErrorMessage(error)));
    }
  };

export const initiateConversation =
  (values: WhatsappTemplateRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(whatsappActions.setLoadingInitiate());
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        "/whatsapp/initiate",
        values,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Template queued for delivery");
        dispatch(whatsappActions.initiateSuccess());
        dispatch(
          whatsappActions.setServerStatus({
            operation: "initiate",
            status: 200,
          }),
        );
      }
    } catch (error) {
      dispatch(whatsappActions.setErrorInitiate(getErrorMessage(error)));
    }
  };

export const replyToConversation =
  (payload: UpdateWhatsappReplyPayload) => async (dispatch: AppDispatch) => {
    dispatch(whatsappActions.setLoadingReply());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<WhatsappMessageDto>
      >(
        `/whatsapp/conversations/${payload.conversationId}/reply`,
        payload.values,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(whatsappActions.replySuccess(data.data));
      }
    } catch (error) {
      dispatch(whatsappActions.setErrorReply(getErrorMessage(error)));
    }
  };

export const resolveConversation =
  (conversationId: string) => async (dispatch: AppDispatch) => {
    dispatch(whatsappActions.setLoadingResolve());
    try {
      const { data } = await axiosInstance.patch<ApiResponseDto<void>>(
        `/whatsapp/conversations/${conversationId}`,
        null,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success("Conversation resolved");
        dispatch(whatsappActions.resolveSuccess(conversationId));
      }
    } catch (error) {
      dispatch(whatsappActions.setErrorResolve(getErrorMessage(error)));
    }
  };

export const resetWhatsappServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(whatsappActions.setServerStatus(null));
};
